import { Guest } from '@adminModels';
import { CreateGuestData, UpdateGuestData } from '@adminStore/guests';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiConfiguration } from '@klubovna/api-client';
import {
  guestsControllerCreate,
  GuestsControllerCreate$Params,
  guestsControllerDelete,
  guestsControllerDeletePhoto,
  guestsControllerFindAll,
  guestsControllerFindOne,
  guestsControllerUpdate,
  GuestsControllerUpdate$Params,
  guestsControllerUploadPhoto,
  GuestsControllerUploadPhoto$Params,
} from '@klubovna/api-client';
import { PaginatedResponse, toPaginatedResponse } from '@klubovna/api-client';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface GuestsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
  sortBy?: 'name' | 'roleSk' | 'roleEn' | 'createdAt' | 'isActive';
  sortOrder?: 'ASC' | 'DESC';
}

@Injectable({
  providedIn: 'root',
})
export class GuestsApiService {
  private http = inject(HttpClient);
  private apiConfig = inject(ApiConfiguration);

  findAll(params?: GuestsQueryParams): Observable<PaginatedResponse<Guest>> {
    const apiParams = {
      page: params?.page,
      limit: params?.limit,
      search: params?.search,
      isActive: params?.isActive,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    };
    return guestsControllerFindAll(this.http, this.apiConfig.rootUrl, apiParams)
      .pipe(map(response => toPaginatedResponse<Guest>(response.body)));
  }

  findOne(id: string): Observable<Guest> {
    return guestsControllerFindOne(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(response => response.body as Guest));
  }

  create(guestData: CreateGuestData): Observable<Guest> {
    const params: GuestsControllerCreate$Params = {
      body: guestData,
    };
    return guestsControllerCreate(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as Guest));
  }

  update(id: string, guestData: UpdateGuestData): Observable<Guest> {
    const params: GuestsControllerUpdate$Params = {
      id,
      body: guestData,
    };
    return guestsControllerUpdate(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as Guest));
  }

  delete(id: string): Observable<void> {
    return guestsControllerDelete(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(() => undefined));
  }

  uploadPhoto(id: string, file: File): Observable<Guest> {
    const params: GuestsControllerUploadPhoto$Params = {
      id,
      body: { file },
    };
    return guestsControllerUploadPhoto(this.http, this.apiConfig.rootUrl, params)
      .pipe(map(response => response.body as Guest));
  }

  deletePhoto(id: string): Observable<Guest> {
    return guestsControllerDeletePhoto(this.http, this.apiConfig.rootUrl, { id })
      .pipe(map(response => response.body as Guest));
  }
}
